/**
 * Tree helpers for nested text prompt groups (labels + AI-selectable leaves)
 * For Media Devoted | Created By Ciro Autiero
 */

/** Minimal shape for tree logic (nested text + sender prompt groups). */
export type TreeGroupLike = {
  id: string;
  name: string;
  is_active: boolean;
  prompt: string | null;
  parent_id?: string | null;
};

function parentKey(g: TreeGroupLike): string | null {
  return g.parent_id ?? null;
}

export function mergedPromptChain(
  groupId: string,
  byId: Map<string, TreeGroupLike>,
): string {
  const parts: string[] = [];
  let currentId: string | null = groupId;
  const seen = new Set<string>();
  while (currentId) {
    if (seen.has(currentId)) break;
    seen.add(currentId);
    const g = byId.get(currentId);
    if (!g) break;
    const t = (g.prompt ?? '').trim();
    if (t) parts.push(t);
    currentId = parentKey(g);
  }
  parts.reverse();
  return parts.join('\n\n');
}

export function displayPath(groupId: string, byId: Map<string, TreeGroupLike>): string {
  const names: string[] = [];
  let currentId: string | null = groupId;
  const seen = new Set<string>();
  while (currentId) {
    if (seen.has(currentId)) break;
    seen.add(currentId);
    const g = byId.get(currentId);
    if (!g) break;
    names.unshift(g.name);
    currentId = parentKey(g);
  }
  return names.join(' / ');
}

export function parentIdsWithChildren(groups: TreeGroupLike[]): Set<string> {
  const s = new Set<string>();
  for (const g of groups) {
    const p = parentKey(g);
    if (p) s.add(p);
  }
  return s;
}

export function getActiveSelectableLeafGroups<T extends TreeGroupLike>(groups: T[]): T[] {
  const byId = new Map<string, TreeGroupLike>(groups.map((g) => [g.id, g]));
  const hasKids = parentIdsWithChildren(groups);
  return groups.filter(
    (g) =>
      g.is_active &&
      !hasKids.has(g.id) &&
      mergedPromptChain(g.id, byId).trim() !== '',
  );
}

export function optionLabelForGroup(group: TreeGroupLike, groups: TreeGroupLike[]): string {
  const byId = new Map(groups.map((g) => [g.id, g]));
  return displayPath(group.id, byId);
}

/** Folder: short name. Root leaf: name. Nested leaf: full path (aligned with AI picker labels). */
export function groupTreeCardTitle(
  group: TreeGroupLike,
  groups: TreeGroupLike[],
  isFolder: boolean,
): string {
  if (isFolder) return group.name;
  const byId = new Map(groups.map((g) => [g.id, g]));
  if (parentKey(group) == null) return group.name;
  return displayPath(group.id, byId);
}

export function descendantIdsIncludingSelf(
  rootId: string,
  groups: TreeGroupLike[],
): Set<string> {
  const byParent = new Map<string | null, TreeGroupLike[]>();
  for (const g of groups) {
    const k = g.parent_id ?? null;
    const list = byParent.get(k) ?? [];
    list.push(g);
    byParent.set(k, list);
  }
  const out = new Set<string>();
  const walk = (id: string) => {
    out.add(id);
    for (const c of byParent.get(id) ?? []) walk(c.id);
  };
  walk(rootId);
  return out;
}

/** Selectable leaves whose ancestor chain includes `parentId` (folder subtree). */
export function getActiveSelectableLeafGroupsUnderParent<T extends TreeGroupLike>(
  groups: T[],
  parentId: string,
): T[] {
  const desc = descendantIdsIncludingSelf(parentId, groups);
  const leaves = getActiveSelectableLeafGroups(groups);
  return leaves.filter((l) => desc.has(l.id)) as T[];
}

/** Folders that have ≥1 active selectable leaf under them (randomize-within-folder scope). */
export function getRandomizeScopeFolderOptions(groups: TreeGroupLike[]): { id: string; name: string }[] {
  const hasKids = parentIdsWithChildren(groups);
  const byId = new Map(groups.map((g) => [g.id, g]));
  const folders = groups.filter((g) => hasKids.has(g.id));
  const out: { id: string; name: string }[] = [];
  for (const f of folders) {
    if (getActiveSelectableLeafGroupsUnderParent(groups, f.id).length > 0) {
      out.push({ id: f.id, name: displayPath(f.id, byId) });
    }
  }
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return out;
}
